import { useEffect, useState } from 'react';
import { useParams } from 'react-router-dom';
import axios from 'axios';
import { Check, Mic } from 'lucide-react';
import { International, TUNNEL_STYLE, TunnelHeader } from '@/components/tunnel/TunnelCommun';
import { DemoCaptures } from '@/components/tunnel/DemoCaptures';
import { DemandeForm } from '@/components/tunnel/DemandeForm';

const API = `${process.env.REACT_APP_BACKEND_URL}/api/public/tunnel`;
const NIVEAUX = [['petite', 'Petite exploitation'], ['moyenne', 'Exploitation moyenne'], ['grande', 'Grande structure']];

const Hero = ({ m }) => (
  <section className="px-6 md:px-12 pt-6 pb-16 reveal">
    <p className="text-xs uppercase tracking-[0.25em] text-[#8a6d12] mb-4">{m.famille} · {m.nom}</p>
    <h1 data-testid="metier-titre" className="text-4xl sm:text-5xl lg:text-6xl text-[#12321f] leading-[1.05] max-w-4xl mb-5">{m.accroche}</h1>
    <p className="text-base md:text-lg text-[#33443a] max-w-2xl mb-6">{m.sous_titre}</p>
    <div className="flex flex-wrap gap-2">{m.sous_types.map((s) => <span key={s} className="rounded-full bg-white px-3 py-1 text-sm ring-1 ring-black/5">{s}</span>)}</div>
  </section>
);

const Douleurs = ({ m }) => (
  <section className="px-6 md:px-12 py-14 bg-[#ece5d3]">
    <h2 className="text-3xl md:text-4xl text-[#12321f] mb-8">Ce qui vous prend du temps aujourd'hui</h2>
    <div className="grid md:grid-cols-2 gap-4">{m.douleurs.map((d) => <p key={d} className="rounded-2xl bg-white/70 p-5 text-[#33443a]">« {d} »</p>)}</div>
  </section>
);

const Dictee = ({ m }) => (
  <section data-testid="metier-dictee" className="px-6 md:px-12 py-16 grid md:grid-cols-2 gap-8 items-center">
    <div className="rounded-3xl bg-[#12321f] p-8 text-white">
      <Mic className="h-8 w-8 text-[#c9a227] mb-4" />
      <p className="serif text-2xl leading-snug">« {m.dictee} »</p>
    </div>
    <div>
      <p className="text-xs uppercase tracking-[0.25em] text-[#8a6d12] mb-3">Et Valentine fait le reste</p>
      <p className="text-lg text-[#17261b]">{m.resultat_dictee}</p>
      <p className="mt-4 text-sm text-[#4b5a4f]">Gain attendu : {m.gain}</p>
    </div>
  </section>
);

const Modules = ({ m }) => (
  <section className="px-6 md:px-12 py-14">
    <h2 className="text-3xl md:text-4xl text-[#12321f] mb-8">Ce que Bureau Agricole fait pour vous</h2>
    <div className="grid md:grid-cols-2 lg:grid-cols-3 gap-4">
      {m.modules.map((x) => (
        <div key={x.titre} className="rounded-2xl bg-white p-6 ring-1 ring-black/5">
          <Check className="h-5 w-5 text-[#3f6212] mb-3" /><h3 className="text-lg text-[#12321f] mb-2">{x.titre}</h3><p className="text-sm text-[#4b5a4f]">{x.texte}</p>
        </div>
      ))}
    </div>
    <p className="mt-8 rounded-2xl border-l-4 border-[#c9a227] bg-white p-5 text-sm text-[#33443a]"><b>Réglementation :</b> {m.reglementation}</p>
  </section>
);

const Niveaux = ({ m }) => (
  <section data-testid="metier-niveaux" className="px-6 md:px-12 py-14 bg-[#ece5d3]">
    <h2 className="text-3xl md:text-4xl text-[#12321f] mb-8">À chaque taille d'exploitation sa formule</h2>
    <div className="grid md:grid-cols-3 gap-4">
      {NIVEAUX.map(([k, l]) => <div key={k} className="rounded-2xl bg-white p-6"><h3 className="text-xl text-[#12321f] mb-2">{l}</h3><p className="text-sm text-[#4b5a4f]">{m.niveaux?.[k]}</p></div>)}
    </div>
  </section>
);

export default function SolutionMetierPage() {
  const { slug } = useParams();
  const [m, setM] = useState(null);
  useEffect(() => { axios.get(`${API}/metiers/${slug}`).then((r) => setM(r.data)).catch(() => setM(false)); }, [slug]);
  if (m === false) return <p className="p-12" data-testid="metier-introuvable">Ce métier n'existe pas encore dans notre tunnel.</p>;
  if (!m) return null;
  return (
    <div className="tunnel min-h-screen" data-testid="solution-metier-page">
      <style>{TUNNEL_STYLE}</style>
      <TunnelHeader />
      <Hero m={m} /><Douleurs m={m} /><Dictee m={m} /><DemoCaptures metier={m.nom} /><Modules m={m} /><Niveaux m={m} /><International />
      <section id="demande" className="px-6 md:px-12 py-16 grid lg:grid-cols-2 gap-10 items-start">
        <div>
          <h2 className="text-3xl md:text-4xl text-[#12321f] mb-4">On vous montre sur votre exploitation ?</h2>
          <p className="text-[#33443a]">Julien ou Marcelle vous rappelle sous 24 h pour une démo sur place ou une visio de 30 minutes. Sans engagement.</p>
        </div>
        <DemandeForm metier={m} />
      </section>
    </div>
  );
}
